import { useEffect, useRef, useState, useMemo } from 'react';
import { Connection, SaveConnectionOptions } from './types/electron';
import { isBrowserMode, BrowserModeConnectionsDialog, BrowserModeDemoBanner, useEmbedAutoConnect } from './features/browser-mode';
import { NewConnectionPopup } from './features/connections/components/NewConnectionPopup';
import { AvailableConnectionsPopup } from './features/connections/components/AvailableConnectionsPopup';
import { Button } from './components/ui/button';
import { TooltipProvider } from './components/ui/tooltip';
import { Toaster } from './components/ui/toaster';
import { EntraDeviceCodeDialog } from './features/connections/components/EntraDeviceCodeDialog';
import { ConnectPasswordPromptDialog } from './features/connections/components/ConnectPasswordPromptDialog';
import { AzureImportPicker, ImportConnectionsDialog } from './features/connections/connection-import';
import { DockerDiscoveryDialog } from './features/connections/docker-discovery';
import { MainShell } from './layout/components/MainShell';
import { SidebarContent } from './layout/components/SidebarContent';
import { useWindowTitle } from './layout/hooks/useWindowTitle';
import { Workspace } from './features/workspace';
import { DatabaseExplorer } from './features/database-objects/components/DatabaseExplorer';
import { TableViewer } from './features/database-objects/components/TableViewer';
import { SplashScreen } from './components/SplashScreen';
import { KeychainExplanationDialog } from './core/security';
import { isTestStoreExposureEnabled } from './core/testMode/isTestStoreExposureEnabled';
import { useConnectionStore } from './features/connections/store/connectionStore';
import { reportStartupReady, markRestoreStarted, markStartupDecision } from './core/analytics/startupReady';
import { reportStartupBreakdown, markConnectionsLoaded, markSplashHidden } from './core/analytics/startupBreakdown';
import { useHangContextPublisher } from './core/analytics/useHangContextPublisher';
import { useConnectionsUIStore } from './features/connections/store/uiStore';
import { useConnectionStringPrefillStore } from './features/connections/store/connectionStringPrefillStore';
import { useDatabaseStore } from './features/database-objects/store/databaseStore';
import { useStorageMutationSync } from './features/database-objects/hooks/useStorageMutationSync';
import { StorageCreateContainerDialog } from './features/workspace/storage-explorer/mutations';
import { useDatabaseUIStore } from './features/database-objects/store/uiStore';
import { initializeConnectionSyncManager } from './features/connections/services/connectionSyncManager';
import { initializePgContextSwitcher, disposePgContextSwitcher } from './features/connections/services/language-services/pgContextSwitcher';
import {
  AuthDialog,
  ProUpgradePromptDialog,
  useAuthCallback,
  TrialOnboardingDialog,
  TrialExpiryDialog,
  useAnalyticsTierSync,
  useDeviceRegistration,
  useEntitlementSync,
  useHasProAccess,
  useLicenseModeSync,
  useSignedTokenSync,
  useSubscriptionSync,
  useTrialOnboarding,
  useTrialExpiryDialog,
} from './core/licensing';
import {
  useSessionStore,
  useSessionsUIStore,
  SessionsDialog,
  StartFreshConfirmDialog,
  SessionValidationDialog,
  initializeSessionAutoSave,
  saveSessionImmediately,
  registerAppResetCallback,
  applyConnectionDecisions,
  applyResumeExclusions,
  getConnectedConnectionIds,
  type ValidationResult,
  type FailedConnectionDecision,
} from './features/sessions';
import { useDisconnectWithConfirmation } from './features/connections/hooks/useDisconnectWithConfirmation';
import { DisconnectConfirmDialog } from './features/connections/components/DisconnectConfirmDialog';
import { useWorkspaceStore } from './features/workspace';
import { requestTabsClose } from './features/workspace/core/store/tabCloseGuardStore';
import { AiWorkspaceSyncHost } from './features/terminal/components/AiWorkspaceSyncHost';
import { DatabaseListingLifecycleHost } from './features/database-objects/components/DatabaseListingLifecycleHost';
import { useAiTerminalSettingsSync } from './features/terminal/hooks/useAiTerminalSettingsSync';
import { useMcpContextSync, useMcpUiControl, useWriteApprovalStore, McpWriteApprovalDialog, useStorageWriteApprovalStore, useStorageWriteApprovalRequests, McpStorageWriteApprovalDialog, usePairingApprovalStore, McpPairingDialog, useConnectionsAddApprovalStore, McpConnectionsAddApprovalDialog, useMetaInfoDeclareApprovalStore, McpMetaInfoDeclareApprovalDialog, useMetaInfoDeclareApplied } from './features/mcp-context';
import { useTransactionSessionsReleased } from './features/workspace/query-editor/hooks/useTransactionSessionsReleased';
import { useAppStatusReporter } from './features/mcp-context/hooks/useAppStatusReporter';
import { StaleLockMonitor } from './features/terminal/components/StaleLockMonitor';
import { BackupStatusBar, useTaskProgressSubscription } from './features/backup-restore';
import { UpdateDownloadToast } from './features/auto-updates/components/UpdateDownloadToast';
import { UpdateReadyToast } from './features/auto-updates/components/UpdateReadyToast';
import { UpdateFallbackBanner } from './features/auto-updates/components/UpdateFallbackBanner';
import { CrashDialogTestHarness } from './features/crash-dialog/components/CrashDialogTestHarness';
import {
  setAppVersion,
  setAppArch,
  registerStoreAccessors,
} from './features/crash-dialog/services/crashContextCollector';
import { ErrorBoundaryTestHarness } from './features/crash-dialog/components/ErrorBoundaryTestHarness';
import { MainProcessCrashListener } from './features/crash-dialog/components/MainProcessCrashListener';
import { GlobalErrorListener } from './features/crash-dialog/components/GlobalErrorListener';
import { emitSessionSummary } from './features/crash-dialog/services/crashSessionSummary';
import { useAppStore } from './store/appStore';
import { GlobalTableExplorerDialog } from './features/workspace/table-explorer/components/GlobalTableExplorerDialog';
import { GlobalDatabaseBlueprintSetupDialog } from './features/database-blueprint';
import { hydrateBlueprintRegistry } from './features/script-browser/services/hydrateBlueprintRegistry';
import { GlobalRefreshFromDbDialog, GlobalSyncDataDialog, GlobalSyncDataProgressDialog } from './features/workspace/database-blueprint';
import { GlobalMetaInfoManagerDialog } from './features/workspace/common/components/meta-info/GlobalMetaInfoManagerDialog';
import {
  BlastRadiusPanel as VqeDdlBlastRadiusPanel,
  PreviewDialog as VqeDdlPreviewDialog,
} from './features/workspace/visual-ddl-editor/render/common';
import { openInDependencyViewer as openVqeDdlDependencyViewer } from './features/workspace/visual-ddl-editor/dependents';
import { GlobalDeclareJsonColumnDialog } from './features/workspace/common/components/filters/GlobalDeclareJsonColumnDialog';
import { GlobalDeclareEnumColumnDialog } from './features/workspace/common/components/filters/GlobalDeclareEnumColumnDialog';
import { GlobalDeclareSpatialColumnDialog } from './features/workspace/common/components/filters/GlobalDeclareSpatialColumnDialog';
import { GlobalJsonInspectorDialog } from './features/workspace/common/components/meta-info/GlobalJsonInspectorDialog';
import { GlobalEnumDetailsDialog } from './features/workspace/common/components/meta-info/GlobalEnumDetailsDialog';
import { GlobalLooseRelationshipEditorDialog } from './features/workspace/common/components/filters/GlobalLooseRelationshipEditorDialog';
import { GlobalConfirmDialog } from './features/workspace/common/components/confirm/GlobalConfirmDialog';
import { StorageDialogHost } from './features/workspace/storage-explorer/properties';
import { useWhatsNew, WhatsNewDialog } from './features/whats-new';
import { useFileDrop, FileDropOverlay } from './features/file-drop';
import { TransfersPanel, useTransferEvents } from './features/transfers';
import { ConnectionDiagnosticsDialogHost } from './features/connection-diagnostics/components/ConnectionDiagnosticsDialogHost';
import { QueueDialogsHost } from './features/workspace/storage-explorer/messages';
import { FileShareDialogsHost } from './features/workspace/storage-explorer/files';
import { PinConfirmSheet } from './features/workspace/dashboards/components/PinConfirmSheet';
import { initializeEngineSupport } from './features/workspace/job-scheduler/engineConfig';
import type { ActiveConnection } from './features/connections/models/connectionTypes';
import { StorageTableDialogsHost } from './features/workspace/storage-explorer/entities/components/StorageTableDialogsHost';
import { safeConnectionName } from './types/safeConnectionDisplay';

/**
 * Container component for MCP Write Approval Dialog.
 * Connects the dialog to the write approval store.
 */
function McpWriteApprovalDialogContainer() {
  const pendingRequest = useWriteApprovalStore((state) => state.pendingRequest);
  const approveRequest = useWriteApprovalStore((state) => state.approveRequest);
  const denyRequest = useWriteApprovalStore((state) => state.denyRequest);

  return (
    <McpWriteApprovalDialog
      request={pendingRequest}
      onApprove={approveRequest}
      onDeny={denyRequest}
    />
  );
}

function McpStorageWriteApprovalDialogContainer() {
  const request = useStorageWriteApprovalStore((state) => state.pendingRequest);
  const approve = useStorageWriteApprovalStore((state) => state.approveRequest);
  const deny = useStorageWriteApprovalStore((state) => state.denyRequest);
  return <McpStorageWriteApprovalDialog request={request} onApprove={approve} onDeny={deny} />;
}

/**
 * Container component for MCP Pairing Dialog.
 * Connects the dialog to the pairing approval store.
 */
function McpPairingDialogContainer() {
  const pendingRequest = usePairingApprovalStore((state) => state.pendingRequest);
  const approveRequest = usePairingApprovalStore((state) => state.approveRequest);
  const denyRequest = usePairingApprovalStore((state) => state.denyRequest);

  return (
    <McpPairingDialog
      request={pendingRequest}
      onApprove={approveRequest}
      onDeny={denyRequest}
    />
  );
}

/**
 * Container component for MCP Connections Add Approval Dialog.
 * Connects the dialog to the connections-add approval store.
 */
function McpConnectionsAddApprovalDialogContainer() {
  const pendingRequest = useConnectionsAddApprovalStore((state) => state.pendingRequest);
  const approveRequest = useConnectionsAddApprovalStore((state) => state.approveRequest);
  const denyRequest = useConnectionsAddApprovalStore((state) => state.denyRequest);

  return (
    <McpConnectionsAddApprovalDialog
      request={pendingRequest}
      onApprove={approveRequest}
      onDeny={denyRequest}
    />
  );
}

/**
 * Container component for the MCP MetaInfo Declare Approval Dialog.
 * Also hosts the "a declaration landed" subscription, so an approved
 * agent-origin write refreshes the MetaInfo the app is already showing.
 */
function McpMetaInfoDeclareApprovalDialogContainer() {
  const pendingRequest = useMetaInfoDeclareApprovalStore((state) => state.pendingRequest);
  const approveRequest = useMetaInfoDeclareApprovalStore((state) => state.approveRequest);
  const denyRequest = useMetaInfoDeclareApprovalStore((state) => state.denyRequest);
  useMetaInfoDeclareApplied();

  return (
    <McpMetaInfoDeclareApprovalDialog
      request={pendingRequest}
      onApprove={approveRequest}
      onDeny={denyRequest}
    />
  );
}

function App() {
  useTaskProgressSubscription();
  useStorageMutationSync();

  // Get states and actions from feature-specific Zustand stores
  const {
    connections,
    activeConnections,
    loadConnections,
    addConnection,
    disconnectFromDatabase,
    connectToDatabase,
    markConnectionDisconnected,
    loading: connectionsLoading,
  } = useConnectionStore();

  // (The former languageServerStore — a zero-reader SSTS status mirror — was
  // deleted in Plan 20 §10 step 7's fix round: mssql IntelliSense no longer
  // involves SSTS, and with SSTS lazy-launched the store sat at
  // 'uninitialized' forever with no UI attached.)

  const {
    showNewConnectionDialog,
    prefillHandoffId,
    showConnectionsDialog,
    editingConnection,
    setShowNewConnectionDialog,
    setShowConnectionsDialog,
    setEditingConnection,
  } = useConnectionsUIStore();
  
  const {
    selectedNodeId,
    setSelectedNodeId
  } = useDatabaseUIStore();

  const {
    removeConnection
  } = useDatabaseStore();

  // Reactive Pro access status (correctly subscribes to all sources: trial, subscription, grace period)
  const hasProAccess = useHasProAccess();

  // Trial onboarding dialog
  const trialOnboarding = useTrialOnboarding();
  const trialExpiry = useTrialExpiryDialog();

  // What's New dialog (auto-shows after updates)
  const whatsNew = useWhatsNew();

  // Session management
  const {
    currentSession,
    loadSession,
    hasSavedSession,
    clearSession,
    updateConnectionStatus,
    createNewSession,
  } = useSessionStore();

  const {
    showSessionsDialog,
    setShowSessionsDialog,
    openSessionsDialogForStartup,
  } = useSessionsUIStore();

  const restoreTabs = useWorkspaceStore((state) => state.restoreTabs);

  // Crash report mode (used to tag the session-end crash summary)
  const crashReportMode = useAppStore((s) => s.crashReportMode);

  // AI workspace sync hook (syncs query tabs to AI terminal workspaces) is
  // rendered as <AiWorkspaceSyncHost /> below to keep its aiSelectionStore
  // subscription from re-rendering this whole component.
  useAiTerminalSettingsSync();

  // MCP context sync hook (syncs app state to MCP server for AI agent access)
  useMcpContextSync();

  // MCP UI control hook (handles UI commands from MCP server)
  useMcpUiControl();
  useStorageWriteApprovalRequests();

  // A connection teardown that ended transaction sessions resets the affected
  // tabs' transaction state (and says so), so the toolbar never claims a
  // transaction the backend no longer holds.
  useTransactionSessionsReleased();

  // Report blocking dialog state to main process for CLI /status endpoint
  useAppStatusReporter();

  // License mode sync hook (syncs license state to main process for context files)
  useLicenseModeSync();

  // Auth callback hook (handles OAuth deep link callbacks from Electron)
  useAuthCallback();

  // Subscription sync hook (fetches subscription on sign-in)
  useSubscriptionSync();

  // Device registration hook (registers device on sign-in for abuse detection)
  useDeviceRegistration();

  // Entitlement sync hook (syncs trial/subscription state to main process)
  useEntitlementSync();

  // Analytics tier sync hook (keeps the Umami `subscription` tag current)
  useAnalyticsTierSync();

  // Signed token sync hook (fetches and stores signed entitlement tokens)
  useSignedTokenSync();

  // Embed mode auto-connect (for homepage interactive demo)
  useEmbedAutoConnect();

  // Drag-and-drop .sql (script) and data (import) files into the app window
  const { isDragging, dragKind, uploadLabel } = useFileDrop();
  // The ONE transfers subscription: jobs outlive tabs, so it lives here, not in a tab (spec §7.2).
  useTransferEvents();

  // Disconnect with confirmation hook
  const {
    dialogState: disconnectDialogState,
    initiateDisconnect,
    confirmDisconnect,
    cancelDisconnect,
  } = useDisconnectWithConfirmation();

  // Compute all connections for explorer: active + disconnected from session (or saved browser mode connections)
  const allConnectionsForExplorer = useMemo<ActiveConnection[]>(() => {
    // Start with active connections
    const activeConnectionIds = new Set(activeConnections.map(c => c.id));
    const result = [...activeConnections];

    // Add disconnected connections from session that aren't already active
    if (currentSession) {
      for (const sessionConn of currentSession.connections) {
        if (sessionConn.status === 'disconnected' && !activeConnectionIds.has(sessionConn.connectionId)) {
          // Find the connection config
          const connectionConfig = connections.find(c => c.id === sessionConn.connectionId);
          if (connectionConfig) {
            result.push({
              id: sessionConn.connectionId,
              connection: connectionConfig,
              status: 'disconnected',
              error: sessionConn.error,
            });
          }
        }
      }
    }

    // In browser mode, add saved browser mode connections as disconnected
    // so they can be reconnected from the tree after a page reload. Only
    // connections that were actually connected before (lastConnectedAt is
    // stamped on successful connect) qualify — the demo connections seeded
    // for the start page must not show up as phantom "Disconnected" entries.
    if (isBrowserMode()) {
      for (const conn of connections) {
        // Only add previously-connected browser mode SQLite connections that aren't already active
        if (
          conn.engine === 'sqlite' &&
          (conn.server === 'browser-demo' || conn.server === 'browser-upload') &&
          conn.lastConnectedAt &&
          !activeConnectionIds.has(conn.id)
        ) {
          result.push({
            id: conn.id,
            connection: conn,
            status: 'disconnected',
          });
        }
      }
    }

    return result;
  }, [activeConnections, currentSession, connections]);

  // Track if we're restoring a session
  const [isRestoringSession, setIsRestoringSession] = useState(false);
  const [showStartFreshConfirm, setShowStartFreshConfirm] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  /**
   * The session this open of the validation dialog has already started
   * restoring. One open is one restore: a repeated completion for that session
   * is ignored until the dialog is opened again (the 1.5.0 restore loop
   * re-entered `handleValidationComplete` for one resume, ~60×/s). Cleared at
   * both places that open the dialog — resume and session switch.
   */
  const validationRestoreSessionIdRef = useRef<string | null>(null);
  const sessionRestorationAttempted = useRef(false);
  // Renderer-hang context for main (validation dialog open = restore under way).
  useHangContextPublisher(isRestoringSession || showValidationDialog);

  // Keychain explanation dialog (macOS only, first-run)
  const [showKeychainExplanation, setShowKeychainExplanation] = useState(false);
  const keychainCheckDone = useRef(false);

  // Splash screen state - when true, the HTML splash screen is hidden
  const [appReady, setAppReady] = useState(false);

  // Check if Electron API is available
  useEffect(() => {
    if (!window.electronAPI) {
      console.error('Electron API is not available - connections will not be saved');
    }
    // Populate job-scheduler engine support cache from backend registry
    initializeEngineSupport();
  }, []);

  // Initialize crash context: register store accessors, version, and arch
  useEffect(() => {
    // Register store accessors for crash context collection
    registerStoreAccessors(
      () => useWorkspaceStore.getState(),
      () => useConnectionStore.getState()
    );

    // Get version and arch from auto-updates state
    const initAppInfo = async () => {
      try {
        const state = await window.electronAPI?.autoUpdates?.getState();
        if (state?.currentVersion) {
          setAppVersion(state.currentVersion);
        }
        if (state?.arch) {
          setAppArch(state.arch);
        }
      } catch (error) {
        console.warn('[App] Failed to get app info:', error);
      }
    };
    initAppInfo();
  }, []);

  // Hide splash as soon as connections finish loading
  useEffect(() => {
    if (!connectionsLoading) {
      markConnectionsLoaded();
      setAppReady(true);
      markSplashHidden();
      // Fire `app:startup_breakdown` right after the app becomes ready —
      // piggybacks on the same moment `app:startup_ready` fires elsewhere in
      // this component, without touching that (frozen) event's own payload
      // or timing. Guarded internally to once per launch.
      reportStartupBreakdown();
    }
  }, [connectionsLoading]);

  // Check if keychain explanation dialog should be shown (macOS only, first-run)
  useEffect(() => {
    // Skip in browser mode
    if (isBrowserMode()) return;
    // Skip in E2E test mode (unless explicitly enabled). The Keychain dialog is
    // modal and blocks clicks, which makes most E2E specs fail after reloads.
    const isTestMode = window.electronAPI?.testMode?.isTestMode?.() === true;
    const allowDialogInTests = window.localStorage.getItem('sql-studio-e2e-allow-keychain-explanation') === '1';
    if (isTestMode && !allowDialogInTests) return;
    // Only check once
    if (keychainCheckDone.current) return;
    keychainCheckDone.current = true;

    const checkKeychainExplanation = async () => {
      try {
        const needsExplanation = await window.electronAPI?.keychain?.needsExplanation();
        if (needsExplanation) {
          setShowKeychainExplanation(true);
        }
      } catch (error) {
        console.error('[App] Error checking keychain explanation:', error);
      }
    };

    checkKeychainExplanation();
  }, []);

  // Handle keychain explanation dialog dismissal
  const handleKeychainExplanationContinue = async () => {
    setShowKeychainExplanation(false);
    try {
      await window.electronAPI?.keychain?.markExplanationShown();
    } catch (error) {
      console.error('[App] Error marking keychain explanation shown:', error);
    }
  };

  // Cleanup body pointer-events when all dialogs are closed
  // This fixes an issue where Radix Dialog doesn't properly cleanup when multiple dialogs close simultaneously
  useEffect(() => {
    const allDialogsClosed = !showSessionsDialog && !showStartFreshConfirm && !showValidationDialog && !showNewConnectionDialog && !showConnectionsDialog && !editingConnection && !disconnectDialogState && !showKeychainExplanation;
    if (allDialogsClosed) {
      // Small delay to let Radix finish its cleanup, then force reset if needed
      const forcePointerEventsCleanup = () => {
        // If any Radix overlay is still open, don't override its pointer-events lock.
        if (document.querySelector('[data-state="open"][aria-hidden="true"]')) return;

        const htmlPointerEvents = getComputedStyle(document.documentElement).pointerEvents;
        const bodyPointerEvents = getComputedStyle(document.body).pointerEvents;

        if (htmlPointerEvents === 'none') {
          document.documentElement.style.pointerEvents = 'auto';
        } else if (document.documentElement.style.pointerEvents === 'none') {
          document.documentElement.style.pointerEvents = '';
        }

        if (bodyPointerEvents === 'none') {
          document.body.style.pointerEvents = 'auto';
        } else if (document.body.style.pointerEvents === 'none') {
          document.body.style.pointerEvents = '';
        }
      };

      const timers = [
        setTimeout(forcePointerEventsCleanup, 100),
        setTimeout(forcePointerEventsCleanup, 350),
      ];

      return () => {
        timers.forEach((timer) => clearTimeout(timer));
      };
    }
  }, [showSessionsDialog, showStartFreshConfirm, showValidationDialog, showNewConnectionDialog, showConnectionsDialog, editingConnection, disconnectDialogState, showKeychainExplanation]);

  // Global pointer-events safety net: independent of which dialog is open.
  // Watches `body` / `html` inline styles and clears a stuck `pointer-events: none`
  // whenever no Radix dialog is actually open. This catches dialogs that aren't
  // tracked by the React state above (the global JSON-declare dialog, the
  // MetaInfo manager + its nested ConfirmDialog, etc.). Without this, opening
  // one of those from a context-menu (or stacking an AlertDialog inside a
  // Dialog) can leave the body pointer-events locked forever — page looks fine,
  // text is selectable, but no clicks register.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    let scheduledAt = 0;
    const checkAndClear = () => {
      scheduledAt = 0;
      // If any Radix dialog/alert-dialog content is currently open, leave the
      // pointer-events lock alone — Radix is intentionally blocking background
      // clicks while a modal is up.
      const anyDialogOpen = document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
      );
      if (anyDialogOpen) return;

      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
      if (document.documentElement.style.pointerEvents === 'none') {
        document.documentElement.style.pointerEvents = '';
      }
    };

    const schedule = () => {
      if (scheduledAt) return;
      // Defer past Radix's own cleanup tick so we don't fight an actively
      // closing dialog mid-animation. The 250ms delay covers the dialog
      // close animation (~150ms) plus a margin for downstream cleanup.
      scheduledAt = window.setTimeout(checkAndClear, 250);
    };

    const onMutations = (mutations: MutationRecord[]) => {
      for (const m of mutations) {
        if (m.type !== 'attributes') continue;
        const target = m.target as HTMLElement;
        if (m.attributeName === 'style') {
          if (
            (target === document.body || target === document.documentElement) &&
            target.style.pointerEvents === 'none'
          ) {
            schedule();
          }
        } else if (m.attributeName === 'data-state') {
          // A modal content element just transitioned. If it's now closed,
          // re-check whether body pointer-events got left locked.
          const role = target.getAttribute('role');
          if (
            (role === 'dialog' || role === 'alertdialog') &&
            target.getAttribute('data-state') === 'closed'
          ) {
            schedule();
          }
        }
      }
    };

    // Two observers, deliberately: a second observe() call on the SAME node
    // REPLACES the previous options (DOM spec), so registering the subtree
    // data-state watch on the first observer would silently drop the body
    // style watch. Separate observers also keep the style branch off the
    // subtree firehose.
    const styleObserver = new MutationObserver(onMutations);
    styleObserver.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    styleObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    const stateObserver = new MutationObserver(onMutations);
    stateObserver.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    });

    return () => {
      styleObserver.disconnect();
      stateObserver.disconnect();
      if (scheduledAt) window.clearTimeout(scheduledAt);
    };
  }, []);

  // Register for test reset callback (E2E tests use this to reset app state between tests)
  useEffect(() => {
    const cleanup = registerAppResetCallback(() => {
      console.log('[App] Test reset triggered, resetting internal state');
      didAutoOpenConnectionDialog.current = false;
      sessionRestorationAttempted.current = false;
      setIsRestoringSession(false);
      setShowStartFreshConfirm(false);
      setShowValidationDialog(false);
    });
    return cleanup;
  }, []);

  // Expose internal refs for E2E tests (gated to test/dev — never production).
  useEffect(() => {
    if (isTestStoreExposureEnabled(typeof window !== 'undefined' ? window : undefined)) {
      // @ts-expect-error - Test mode only
      if (!window.__TEST_STORES__) window.__TEST_STORES__ = {};
      // @ts-expect-error - Test mode only
      window.__TEST_STORES__.appRefs = {
        didAutoOpenConnectionDialog,
        sessionRestorationAttempted,
        setIsRestoringSession,
      };
    }
  }, []);

  // Set window title from the active workspace tab (CH-03)
  useWindowTitle();

  // Load connections when the app starts
  useEffect(() => {
    void loadConnections();
  }, [loadConnections]);

  // Project headless/cloned blueprints from the main-process registry into
  // the Scripts panel on startup (Part A Slice 1). Best-effort.
  useEffect(() => {
    void hydrateBlueprintRegistry();
  }, []);

  const didAutoOpenConnectionDialog = useRef(false);

  useEffect(() => {
    // Skip session dialog in test mode (persistent flag survives reloads)
    const skipSessionDialog = localStorage.getItem('sql-studio-skip-session-dialog') === '1';

    console.log('[App] Dialog auto-open check:', {
      didAutoOpen: didAutoOpenConnectionDialog.current,
      connectionsLoading,
      connectionsCount: connections.length,
      isRestoringSession,
      hasSavedSession: hasSavedSession(),
      sessionRestorationAttempted: sessionRestorationAttempted.current,
      skipSessionDialog,
      isBrowserMode: isBrowserMode(),
    });

    // Browser mode: skip sessions flow. Show StartPage and connections dialog on first load.
    // User must manually connect - no auto-reconnect.
    if (isBrowserMode()) {
      if (didAutoOpenConnectionDialog.current) {
        return;
      }

      // Wait for connections to load
      if (connectionsLoading) {
        return;
      }

      didAutoOpenConnectionDialog.current = true;

      // Browser mode: Reset workspace to only StartPage (clear any stale session state)
      // This ensures we always start fresh on the start page, not on a query tab
      const { restoreTabs } = useWorkspaceStore.getState();
      restoreTabs([], null); // Clear all tabs, restoreTabs will add StartPage
      useWorkspaceStore.getState().ensureStartPage();

      // Always show the connections dialog - user must manually connect
      setShowConnectionsDialog(true);
      return;
    }

    if (skipSessionDialog) {
      sessionRestorationAttempted.current = true;

      // In test mode, skip session dialog and show StartPage instead
      if (connectionsLoading) {
        console.log('[App] Test mode: still loading connections, waiting');
        return;
      }

      if (didAutoOpenConnectionDialog.current) {
        return;
      }

      // Show StartPage (not connections dialog) - users can add connections from there
      didAutoOpenConnectionDialog.current = true;
      console.log('[App] Test mode: showing StartPage');
      useWorkspaceStore.getState().ensureStartPage();
      return;
    }

    if (didAutoOpenConnectionDialog.current) {
      console.log('[App] Already auto-opened, skipping');
      return;
    }

    // If there are active connections, we're past startup - don't show session dialog
    // This prevents dialog from appearing during reconnection attempts
    if (activeConnections.length > 0) {
      console.log('[App] Has active connections, not showing startup dialog');
      didAutoOpenConnectionDialog.current = true;
      return;
    }

    // Wait for session restoration to complete before showing dialogs
    if (isRestoringSession) {
      console.log('[App] Session restoration in progress, waiting');
      return;
    }

    // After session restoration completes (either resume or start fresh), don't auto-show dialogs
    if (sessionRestorationAttempted.current) {
      console.log('[App] Session restoration already handled, not auto-showing dialogs');
      return;
    }

    if (connectionsLoading) {
      console.log('[App] Still loading connections, waiting');
      return;
    }

    // If there's a saved session, show the Sessions dialog — unless the user
    // opted out ("Always start a new session"), which takes the startup-dismiss
    // route directly: nothing restored, nothing deleted, the saved session
    // stays resumable via the toolbar Sessions button.
    if (hasSavedSession()) {
      if (useAppStore.getState().startupSessionBehavior === 'new-session') {
        console.log('[App] Startup behavior is new-session, skipping sessions dialog');
        didAutoOpenConnectionDialog.current = true;
        window.electronAPI?.analytics?.track?.('feature:sessions_startup_skipped')?.catch(() => {});
        useWorkspaceStore.getState().ensureStartPage();
        markStartupDecision();
        reportStartupReady('start_fresh');
        return;
      }
      console.log('[App] Saved session exists, showing sessions dialog');
      didAutoOpenConnectionDialog.current = true;
      openSessionsDialogForStartup();
      return;
    }

    // No session to restore — the sessions dialog would only say "No sessions
    // to restore yet" in front of the user, so land directly on the start
    // page instead. The dialog stays reachable via the toolbar Sessions
    // button and still auto-opens when a saved session exists (above).
    if (connections.length === 0) {
      console.log('[App] No connections and no session, showing start page');
      didAutoOpenConnectionDialog.current = true;
      // First-run funnel: the app booted with zero saved connections — the
      // black box between app:start and connection:add starts here. Fires
      // once per app run (guarded by didAutoOpenConnectionDialog).
      window.electronAPI?.analytics?.track?.('ui:empty_state_shown')?.catch(() => {});
      useWorkspaceStore.getState().ensureStartPage();
      reportStartupReady('empty');
      return;
    }

    // Has saved connections but no session - also land on the start page
    // (their connections are listed there).
    console.log('[App] Has connections but no session, showing start page');
    didAutoOpenConnectionDialog.current = true;
    useWorkspaceStore.getState().ensureStartPage();
    reportStartupReady('saved_connections');
  }, [
    activeConnections.length,
    connections.length,
    connectionsLoading,
    setShowConnectionsDialog,
    setShowNewConnectionDialog,
    openSessionsDialogForStartup,
    isRestoringSession,
    hasSavedSession,
  ]);

  // Browser mode: keep start page visible after connecting.
  // User can open query tabs from the start page when ready.
  // This effect is intentionally a no-op in browser mode to preserve the start page.

  // Initialize connection sync when app loads
  useEffect(() => {
    // Initialize ConnectionSyncManager to keep database connections in sync
    initializeConnectionSyncManager();
    // Initialize PostgreSQL context switcher for shared daemon workaround
    initializePgContextSwitcher();

    return () => {
      disposePgContextSwitcher();
    };
  }, []);

  // Initialize session auto-save (debounced saves on workspace changes)
  useEffect(() => {
    const cleanup = initializeSessionAutoSave();
    return cleanup;
  }, []);

  // Save session immediately when app is closing (bypasses debounce)
  useEffect(() => {
    if (!window.electronAPI?.session) {
      console.log('[App] Session API not available, skipping close handler setup');
      return;
    }

    const cleanup = window.electronAPI.session.onAppClosing(() => {
      console.log('[App] App closing, saving session immediately...');
      try {
        // Use immediate save to ensure any pending changes are captured
        saveSessionImmediately();
        console.log('[App] Session saved successfully');
      } catch (error) {
        console.error('[App] Error saving session:', error);
      }
      // Confirm we're done, allow app to close
      window.electronAPI?.session.confirmClose();
    });

    return cleanup;
  }, []);

  // Save session when auto-update is about to restart the app
  useEffect(() => {
    if (!window.electronAPI?.session?.onAppClosingForUpdate) {
      return;
    }

    const cleanup = window.electronAPI.session.onAppClosingForUpdate(() => {
      console.log('[App] Auto-update restart, saving session immediately...');
      try {
        saveSessionImmediately();
        console.log('[App] Session saved successfully for update');
      } catch (error) {
        console.error('[App] Error saving session for update:', error);
      }
      // Confirm we're done, allow update install to proceed
      window.electronAPI?.session.confirmCloseForUpdate?.();
    });

    return cleanup;
  }, []);

  // Mirror the crash-report preference into main. Main-side reporters — the
  // renderer freeze watchdog — report about a renderer that is too stuck to be
  // asked anything, so 'never'/'always'/'ask' has to be known on that side too.
  // Runs on mount as well as on change, so a rehydrated store lands.
  useEffect(() => {
    // `.catch`: the handler registers late in main's startup; a mount-time
    // invoke that loses that race must not become a renderer unhandledrejection.
    void window.electronAPI?.crashDialog?.setReportMode?.(crashReportMode)?.catch?.(() => {});
  }, [crashReportMode]);

  // On window unload, emit one `app:crash-session-summary` event per
  // crash signature with count > 1. This lets the backend see looping
  // crash volumes without being flooded by duplicate full reports.
  useEffect(() => {
    const handler = () => {
      emitSessionSummary((name, data) => {
        void window.electronAPI?.analytics?.track?.(name, data);
      }, crashReportMode);
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [crashReportMode]);

  // Handler for resuming session from SessionsDialog
  const handleResumeSession = (excludedConnectionIds: string[] = []) => {
    setShowSessionsDialog(false);

    if (excludedConnectionIds.length > 0) {
      void applyResumeExclusions(excludedConnectionIds).then(resumeCurrentSession);
      return;
    }

    resumeCurrentSession();
  };

  const resumeCurrentSession = () => {
    // Read through the store: `applyResumeExclusions` may have just replaced
    // the session, which the render-closure copy wouldn't reflect.
    const currentSession = useSessionStore.getState().currentSession;

    // Connections that are already open have nothing to validate:
    // `connectToDatabase` short-circuits for them, so the dialog would flash
    // open and complete in the same tick (and `loadSession` reconnects
    // anything that still needs it anyway). Reachable since the featured card
    // resumes a session that isn't restored even when its connections are
    // live — e.g. the user dismissed the startup dialog, connected by hand,
    // then came back to Sessions from the toolbar.
    // Status-filtered on purpose: an entry that is present but disconnected
    // (dropped connection, Entra placeholder, deleted config) still needs the
    // validation dialog — that's the user's only Retry / Edit / Keep / Remove UI.
    const connectedIds = new Set(
      getConnectedConnectionIds(useConnectionStore.getState().activeConnections)
    );
    const nothingToValidate =
      !!currentSession &&
      currentSession.connections.length > 0 &&
      currentSession.connections.every((conn) => connectedIds.has(conn.connectionId));

    // Skip validation dialog if there are no connections to validate
    // This avoids a Radix UI bug where dialog opens and immediately closes
    if (!currentSession || currentSession.connections.length === 0 || nothingToValidate) {
      sessionRestorationAttempted.current = true;
      setIsRestoringSession(true);
      markRestoreStarted();
      loadSession()
        .then((restored) => {
          if (restored) {
            console.log('[App] Session restored successfully (no connections)');
          }
          // Only create StartPage if the session has no tabs
          if (useWorkspaceStore.getState().tabs.length === 0) {
            useWorkspaceStore.getState().ensureStartPage();
          }
        })
        .catch((error) => {
          console.error('[App] Error restoring session:', error);
          setShowConnectionsDialog(true);
        })
        .finally(() => {
          setIsRestoringSession(false);
          reportStartupReady('session_restore');
        });
      return;
    }

    // Delay opening validation dialog to allow Radix Dialog to properly unmount
    // This works around a Radix UI bug where simultaneous dialog transitions cause infinite loops
    requestAnimationFrame(() => {
      validationRestoreSessionIdRef.current = null;
      setShowValidationDialog(true);
    });
  };

  // Switching sessions closes every tab and disconnects every connection: an
  // open or starting transaction asks first, like closing its tab would
  // (`requestTabsClose`); Cancel keeps the current session.
  const handleOpenSession = (sessionId: string, excludedConnectionIds: string[] = []) => {
    if (sessionId === currentSession?.id) {
      void openSessionNow(sessionId, excludedConnectionIds);
      return;
    }
    setShowSessionsDialog(false);
    requestTabsClose(
      useWorkspaceStore.getState().tabs.map((tab) => tab.id),
      () => { void openSessionNow(sessionId, excludedConnectionIds); },
      { allOrNothing: true },
    );
  };

  // Handler for opening a different session (Pro mode)
  const openSessionNow = async (sessionId: string, excludedConnectionIds: string[] = []) => {
    setShowSessionsDialog(false);
    sessionRestorationAttempted.current = true;

    // If opening a different session, prepare the switch first
    if (sessionId !== currentSession?.id) {
      setIsRestoringSession(true);
      console.log('[App] Preparing to switch to session:', sessionId);

      try {
        // Prepare the switch (save current, disconnect, set target as current)
        // This does NOT reconnect - validation dialog will handle that
        const { prepareSessionSwitch } = useSessionStore.getState();
        const prepared = await prepareSessionSwitch(sessionId);
        if (!prepared) {
          console.log('[App] Failed to prepare session switch');
          setShowConnectionsDialog(true);
          setIsRestoringSession(false);
          return;
        }
        console.log('[App] Session switch prepared, showing validation dialog');
      } catch (error) {
        console.error('[App] Error preparing session switch:', error);
        setShowConnectionsDialog(true);
        setIsRestoringSession(false);
        return;
      } finally {
        setIsRestoringSession(false);
      }
    }

    // Drop the connections the user switched off on the featured card before
    // validation gets a chance to connect them (the target session is now the
    // current one, so exclusions apply to it).
    try {
      await applyResumeExclusions(excludedConnectionIds);
    } catch (error) {
      // A rejection here would strand the switch between "prepared" and
      // "loaded" with auto-save still paused — off for the rest of the run.
      console.error('[App] Error applying resume exclusions:', error);
      useSessionStore.getState().endSessionSwitchWindow();
      setShowConnectionsDialog(true);
      return;
    }

    // Get the updated current session (after prepareSessionSwitch it should be the target)
    const updatedSession = useSessionStore.getState().currentSession;

    // Skip validation dialog if there are no connections to validate
    if (!updatedSession || updatedSession.connections.length === 0) {
      setIsRestoringSession(true);
      markRestoreStarted();
      loadSession()
        .then((restored) => {
          if (restored) {
            console.log('[App] Session restored successfully (no connections)');
          }
          // Only create StartPage if the session has no tabs
          if (useWorkspaceStore.getState().tabs.length === 0) {
            useWorkspaceStore.getState().ensureStartPage();
          }
        })
        .catch((error) => {
          console.error('[App] Error restoring session:', error);
          setShowConnectionsDialog(true);
        })
        .finally(() => {
          setIsRestoringSession(false);
          reportStartupReady('session_restore');
        });
      return;
    }

    // Show validation dialog - same flow for both resume and switch
    // Delay to allow Radix Dialog to properly unmount (workaround for Radix UI bug)
    requestAnimationFrame(() => {
      validationRestoreSessionIdRef.current = null;
      setShowValidationDialog(true);
    });
  };

  // Handler for creating a new session (Pro mode) — closes every tab, so an
  // open or starting transaction asks first (`requestTabsClose`).
  const handleNewSession = () => {
    markStartupDecision();
    setShowSessionsDialog(false);
    sessionRestorationAttempted.current = true;

    requestTabsClose(useWorkspaceStore.getState().tabs.map((tab) => tab.id), () => {
      console.log('[App] Creating new session');
      createNewSession();

      // Show StartPage for the new session
      useWorkspaceStore.getState().ensureStartPage();
      reportStartupReady('start_fresh');
    }, { allOrNothing: true });
  };

  // Handler for when validation completes (with decisions for failed connections)
  const handleValidationComplete = async (
    results: ValidationResult[],
    decisions: FailedConnectionDecision[]
  ) => {
    // Before any state or store write: ignoring a repeat must not feed a loop.
    const restoringSessionId = useSessionStore.getState().currentSession?.id ?? null;
    if (restoringSessionId !== null && validationRestoreSessionIdRef.current === restoringSessionId) {
      console.warn('[App] Ignoring a repeated validation completion for the same resume');
      return;
    }
    validationRestoreSessionIdRef.current = restoringSessionId;
    setShowValidationDialog(false);
    sessionRestorationAttempted.current = true;
    setIsRestoringSession(true);
    markRestoreStarted();

    console.log('[App] Validation complete, loading session...');
    console.log('[App] Validation results:', results);
    console.log('[App] Connection decisions:', decisions);

    try {
      // Apply decisions to current session before loading
      if (currentSession && decisions.length > 0) {
        const updatedSession = applyConnectionDecisions(currentSession, decisions);
        console.log('[App] Applied decisions to session:', {
          originalTabs: currentSession.tabs.length,
          updatedTabs: updatedSession.tabs.length,
          removedConnections: decisions.filter(d => d.decision === 'remove').length,
          keptDisconnected: decisions.filter(d => d.decision === 'keep').length,
        });
        // Update session store with modified session (mirrored into
        // savedSessions so the listed copy doesn't keep the removed connection)
        useSessionStore.getState().replaceCurrentSession(updatedSession);
      }

      // "Keep offline" has to travel with the restore. The decision only marks
      // the session entry `disconnected`, and the restore re-resolves the very
      // same connection as the one it opens eagerly — so without this the app
      // said "queries will be viewable but not executable" and then connected
      // it anyway, password prompt included (codex review, E7).
      const keptOfflineIds = decisions
        .filter((d) => d.decision === 'keep')
        .map((d) => d.connectionId);

      const restored = await loadSession({ keptOfflineIds });
      if (restored) {
        console.log('[App] Session restored successfully');
        // Don't force StartPage if the user closed it previously
        if (useWorkspaceStore.getState().tabs.length === 0) {
          useWorkspaceStore.getState().ensureStartPage();
        }
      } else {
        console.log('[App] No session content to restore');
        // Ensure StartPage even when no session content exists
        useWorkspaceStore.getState().ensureStartPage();
      }
    } catch (error) {
      console.error('[App] Error restoring session:', error);
      // `applyConnectionDecisions` / `replaceCurrentSession` run BEFORE the
      // awaited `loadSession`, so a throw from either never reaches
      // `loadSession`'s own `finally` and would strand a prepared switch with
      // auto-save off for the rest of the run. Idempotent — no-op when no
      // switch holds the pause.
      useSessionStore.getState().endSessionSwitchWindow();
      // If error, show connections dialog as fallback
      setShowConnectionsDialog(true);
    } finally {
      setIsRestoringSession(false);
      reportStartupReady('session_restore');
    }
  };

  // Handler for when user cancels validation
  const handleValidationCancel = () => {
    setShowValidationDialog(false);
    // Cancelling ends a prepared session switch without ever loading it, so
    // nothing else will close the auto-save pause `prepareSessionSwitch`
    // opened — and a pause that is never released is silent data loss for the
    // rest of the app run. No-op when the resume never involved a switch.
    useSessionStore.getState().endSessionSwitchWindow();
    setShowSessionsDialog(true);
  };

  // Handler for starting fresh - shows confirmation dialog
  const handleStartFresh = () => {
    // If there's a current session with tabs or connections, show confirmation
    if (hasSavedSession() || activeConnections.length > 0) {
      setShowStartFreshConfirm(true);
    } else {
      // No session to clear, just proceed
      performStartFresh();
    }
  };

  // Cancel start fresh confirmation
  const handleCancelStartFresh = () => {
    setShowStartFreshConfirm(false);
  };

  // Actually perform the start fresh action after confirmation
  const performStartFresh = async () => {
    markStartupDecision();
    setShowStartFreshConfirm(false);
    setShowSessionsDialog(false);
    sessionRestorationAttempted.current = true;

    // Starting fresh discards everything, including work in progress: withdraw
    // the intent behind any connection still dialing, so it is closed on
    // arrival rather than adopted into the fresh workspace. `activeConnections`
    // below only covers connections that already landed.
    const { abandonConnect } = useConnectionStore.getState();
    for (const conn of useSessionStore.getState().currentSession?.connections ?? []) {
      abandonConnect(conn.connectionId);
    }

    // Disconnect all active connections
    console.log('[App] Disconnecting all connections...');
    for (const conn of activeConnections) {
      try {
        await disconnectFromDatabase(conn.id);
        removeConnection(conn.id);
      } catch (error) {
        console.error(`[App] Failed to disconnect ${conn.id}:`, error);
      }
    }

    // Close all tabs
    console.log('[App] Closing all tabs...');
    restoreTabs([], null);

    // Clear session in Personal mode, keep it in Pro mode
    if (!hasProAccess) {
      console.log('[App] Personal mode: Clearing session data');
      clearSession();
    } else {
      console.log('[App] Pro mode: Session data preserved');
      // Save the current session before clearing workspace
      // (it's already saved since we called saveSessionImmediately on dialog open)
    }

    // Show StartPage for the fresh start
    useWorkspaceStore.getState().ensureStartPage();
    reportStartupReady('start_fresh');
  };

  // The NEW-connection dialog's save. `options` is what THAT save says about
  // itself (`SaveConnectionOptions`) and must be forwarded: for a brand-new
  // connection the SSH secrets the user typed exist ONLY there (round 5, F5) —
  // dropping them saved a tunnelled connection with no bastion credential, so
  // the first connect after Save failed the SSH step.
  const handleSaveConnection = async (
    connection: Connection,
    options?: SaveConnectionOptions,
  ): Promise<boolean> => {
    try {
      const success = await addConnection(connection, options);
      
      if (success) {
        setShowNewConnectionDialog(false);
        
        // Always show the connections popup after successfully saving a connection
        setShowConnectionsDialog(true);
        
        return true;
      } else {
        console.error('Save connection returned false');
        return false;
      }
    } catch (error) {
      console.error('Exception in handleSaveConnection:', error);
      return false;
    }
  };

  const handleCancelNewConnection = () => {
    if (prefillHandoffId) useConnectionStringPrefillStore.getState().cancel(prefillHandoffId);
    setShowNewConnectionDialog(false);
    
    // If we have saved connections, show the connections dialog
    if (connections.length > 0) {
      setShowConnectionsDialog(true);
    }
  };

  const handleDisconnect = async (connectionId: string) => {
    // Find the connection name for the dialog
    const connection = allConnectionsForExplorer.find((c) => c.id === connectionId);
    const connectionName = safeConnectionName(connection?.connection, 'Unknown');

    // Use the disconnect with confirmation hook - it handles tab checking
    // The callback is invoked when disconnect completes (either immediately or after dialog confirmation)
    await initiateDisconnect(connectionId, connectionName, (result) => {
      if (result.success && result.shouldShowConnectionsDialog) {
        setShowConnectionsDialog(true);
      }
    });
  };

  // Handle disconnect confirmation from dialog
  const handleConfirmDisconnect = async () => {
    // The callback passed to initiateDisconnect will handle showing the connections dialog
    await confirmDisconnect();
  };

  const handleReconnect = async (connectionId: string): Promise<boolean> => {
    // Find the saved connection config
    const connection = connections.find((c) => c.id === connectionId);
    if (!connection) {
      console.warn(`[App] Cannot reconnect: connection ${connectionId} not found in saved connections`);
      return false;
    }

    // Browser mode SQLite connections need special handling
    if (isBrowserMode() && connection.engine === 'sqlite') {
      try {
        // Import sqlite utilities dynamically to avoid circular deps
        const { openDatabaseFromBuffer, getCachedDatabase, BROWSER_MODE_DATABASES } = await import('./features/browser-mode');
        const { useBrowserModeStore } = await import('./features/browser-mode/store/browserModeStore');

        // Try to load from cache
        const cachedBytes = await getCachedDatabase(connectionId);
        if (!cachedBytes) {
          // Try to find matching demo database by ID
          const demoDb = BROWSER_MODE_DATABASES.find(db => db.id === connectionId);
          if (demoDb) {
            // For demo databases, reload from asset
            const { loadDemoDatabase } = await import('./web/sqlite');
            const buffer = await loadDemoDatabase(connectionId, demoDb.assetPath, () => {});
            await openDatabaseFromBuffer(connectionId, buffer);
          } else {
            console.warn('[App] Browser mode reconnect: no cached database found for uploaded DB');
            return false;
          }
        } else {
          // Open from cached bytes
          await openDatabaseFromBuffer(connectionId, cachedBytes);
        }

        // Mark as connected in browser mode store
        const browserDb = BROWSER_MODE_DATABASES.find(db => db.id === connectionId);
        if (browserDb) {
          useBrowserModeStore.getState().setConnected(browserDb);
        }

        // Now connect through the normal flow (shim will return success since DB is already open)
        const success = await connectToDatabase(connection);
        if (success) {
          updateConnectionStatus(connectionId, 'connected');
        }
        return success;
      } catch (error) {
        console.error('[App] Browser mode reconnect failed:', error);
        markConnectionDisconnected(connectionId);
        return false;
      }
    }

    // Normal desktop reconnect flow
    const success = await connectToDatabase(connection);
    if (success) {
      // Update session connection status
      updateConnectionStatus(connectionId, 'connected');
    } else {
      // Ensure connection stays marked as disconnected on failure
      // This re-applies the disconnected status to ensure UI consistency
      markConnectionDisconnected(connectionId);
      updateConnectionStatus(connectionId, 'disconnected');
    }
    return success;
  };

  // Handle selection of a tree node in the explorer
  const handleNodeSelect = (nodeId: string) => {
    setSelectedNodeId(nodeId);
    // In a real app, you would load the details of the selected object here
  };

  // Handle refresh request from the explorer
  const handleRefreshRequest = () => {
    // In a real app, you would reload the database objects here
  };

  // Handler for adding a new connection
  const handleAddNewConnection = () => {
    setShowConnectionsDialog(false);
    setShowNewConnectionDialog(true);
  };

  return (
    <TooltipProvider>
    <div className="App h-screen flex flex-col">
      {/* Renders nothing; keeps the AI workspace/selection sync subscription off the App render tree */}
      <AiWorkspaceSyncHost />

      {/* Renders nothing; drops a connection's cached Object Explorer queries when
          its connect ends, whether or not the Explorer pane happens to be mounted */}
      <DatabaseListingLifecycleHost />

      {/* Splash screen during startup */}
      <SplashScreen isReady={appReady} />

      {/* Browser mode demo banner */}
      <BrowserModeDemoBanner />
      <MainShell
        className="flex-1 min-h-0"
        sidebar={
          <SidebarContent
            explorer={
              <DatabaseExplorer
                activeConnections={allConnectionsForExplorer}
                onNodeSelect={handleNodeSelect}
                onRefreshRequest={handleRefreshRequest}
                onAddConnection={() => setShowConnectionsDialog(true)}
                onDisconnect={handleDisconnect}
                onReconnect={handleReconnect}
              />
            }
          />
        }
      >
        <Workspace>
          {/* This is where the content for the selected database object would be displayed */}
          {selectedNodeId ? (
            <TableViewer isLoading={false} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md p-4">
                <h2 className="text-xl font-semibold mb-2">Welcome to Jam SQL Studio</h2>
                <p className="text-muted-foreground mb-4">
                  Connect to a database and select an object from the explorer to get started.
                </p>
                {activeConnections.length === 0 && (
                  <Button onClick={() => setShowConnectionsDialog(true)}>
                    Connect to a Database
                  </Button>
                )}
              </div>
            </div>
          )}
        </Workspace>
      </MainShell>

      {/* Popups */}
      {/* Keychain Explanation Dialog (macOS only, first-run) */}
      <KeychainExplanationDialog
        open={showKeychainExplanation}
        onContinue={handleKeychainExplanationContinue}
      />

      {/* Sessions Dialog (startup flow) */}
      <SessionsDialog
        open={showSessionsDialog && !showNewConnectionDialog && !showConnectionsDialog && !showStartFreshConfirm && !showValidationDialog && !showKeychainExplanation}
        onResume={handleResumeSession}
        onOpenSession={handleOpenSession}
        onStartFresh={handleStartFresh}
        onNewSession={handleNewSession}
        onClose={() => {
          // Dismissing the STARTUP dialog is a terminal startup route — the
          // user proceeds without restoring. Without this, the whole
          // no-restore cohort has no startup row and no resource samples.
          // Gated on isStartupDialog: a toolbar-opened dialog dismissed in a
          // window that never resolved a route (e.g. after a View → Reload)
          // must not mint a mislabeled start_fresh baseline row.
          const wasStartupDialog = useSessionsUIStore.getState().isStartupDialog;
          setShowSessionsDialog(false);
          if (wasStartupDialog) {
            // Land on the Start Page instead of stranding the user on the
            // bare "No active tabs" placeholder (no tabs exist yet at this
            // point in the startup flow). The saved session itself is
            // untouched and stays resumable via the toolbar Sessions button.
            useWorkspaceStore.getState().ensureStartPage();
            markStartupDecision();
            reportStartupReady('start_fresh');
          }
        }}
      />

      {/* Start Fresh Confirmation */}
      <StartFreshConfirmDialog
        open={showStartFreshConfirm}
        onConfirm={performStartFresh}
        onCancel={handleCancelStartFresh}
      />

      {/* Disconnect Confirmation */}
      {disconnectDialogState && (
        <DisconnectConfirmDialog
          open={true}
          connectionName={disconnectDialogState.connectionName}
          affectedTabs={disconnectDialogState.affectedTabs}
          onConfirm={handleConfirmDisconnect}
          onCancel={cancelDisconnect}
        />
      )}

      {/* Session Validation Dialog */}
      {showValidationDialog && currentSession && (
        <SessionValidationDialog
          open={showValidationDialog}
          session={currentSession}
          savedConnections={connections}
          onValidationComplete={handleValidationComplete}
          onCancel={handleValidationCancel}
        />
      )}

      {showNewConnectionDialog && !showConnectionsDialog && (
        <NewConnectionPopup
          onSave={handleSaveConnection}
          onCancel={handleCancelNewConnection}
          noConnections={connections.length === 0}
          prefillHandoffId={prefillHandoffId}
        />
      )}

      {/* Direct per-connection edit dialog (command palette "Edit connection").
          Suppressed while either connections dialog is up — those own their
          local edit flows and stacking two edit forms would fight. */}
      {editingConnection && !showNewConnectionDialog && !showConnectionsDialog && (
        <NewConnectionPopup
          editConnection={editingConnection}
          onSave={async (connection, options) => {
            const success = await useConnectionStore.getState().updateConnection(connection, options);
            if (success) setEditingConnection(null);
            return success;
          }}
          onDelete={async (connectionId) => {
            const success = await useConnectionStore.getState().deleteConnection(connectionId);
            if (success) setEditingConnection(null);
            return success;
          }}
          onCancel={() => setEditingConnection(null)}
        />
      )}

      {showConnectionsDialog && !showNewConnectionDialog && (
        isBrowserMode() ? (
          <BrowserModeConnectionsDialog
            open={true}
            onCancel={() => setShowConnectionsDialog(false)}
          />
        ) : (
          <AvailableConnectionsPopup
            onAddNew={handleAddNewConnection}
            onCancel={() => setShowConnectionsDialog(false)}
          />
        )
      )}

      <EntraDeviceCodeDialog />

      {/* General on-connect password prompt (imported/credential-less connections). */}
      <ConnectPasswordPromptDialog />

      {/* Multi-source connection-import review dialog (empty-state cards, palette, Manage Connections Import… menu). */}
      <ImportConnectionsDialog />

      {/* Azure "signed-in account" import source — opens the shared picker, then feeds the review dialog. */}
      <AzureImportPicker />

      {/* Docker auto-discovery results dialog (New Connection, palette, Manage Connections, empty state). */}
      <DockerDiscoveryDialog />

      {/* Trial onboarding dialog (first launch or weekly reminder) */}
      <TrialOnboardingDialog
        open={trialOnboarding.isDialogOpen}
        isReminder={trialOnboarding.isReminder}
        onStartTrial={trialOnboarding.onStartTrial}
        onContinuePersonal={trialOnboarding.onContinuePersonal}
        onDismissReminder={trialOnboarding.onDismissReminder}
        onSoftDismiss={trialOnboarding.onSoftDismiss}
        isLoading={trialOnboarding.isLoading}
        error={trialOnboarding.error}
        isAuthenticated={trialOnboarding.isAuthenticated}
        userEmail={trialOnboarding.userEmail}
        isResolvingEntitlement={trialOnboarding.isResolvingEntitlement}
        onSignIn={trialOnboarding.onSignIn}
      />

      {/* Trial-expiry dialog (once per expired trial: upgrade / personal / feedback-for-extension) */}
      <TrialExpiryDialog
        open={trialExpiry.isOpen}
        extensionAvailable={trialExpiry.extensionAvailable}
        onUpgrade={trialExpiry.onUpgrade}
        onContinuePersonal={trialExpiry.onContinuePersonal}
        onDismiss={trialExpiry.onDismiss}
        onFeedbackOpened={trialExpiry.onFeedbackOpened}
        onSubmitFeedback={trialExpiry.onSubmitFeedback}
      />

      {/* What's New dialog (auto-shown after app update) */}
      <WhatsNewDialog
        open={whatsNew.isDialogOpen && !showSessionsDialog && !showValidationDialog && !showKeychainExplanation && !trialOnboarding.isDialogOpen && !trialExpiry.isOpen}
        entries={whatsNew.newEntries}
        onDismiss={whatsNew.onDismiss}
      />

      {/* Auth dialog for trial start (shown when user clicks Start Trial without being logged in) */}
      <AuthDialog
        open={trialOnboarding.showAuthDialog}
        onOpenChange={(open) => !open && trialOnboarding.onAuthDialogClose()}
        intent={trialOnboarding.authDialogForTrial ? 'trial' : 'manage'}
      />

      {/* Backup/Restore status bar for minimized operations */}
      <BackupStatusBar />

      {/* Transfers drawer; its chip lives in the app status bar (MainShell → AppStatusBar) */}
      <TransfersPanel />

      {/* Stale AI lock monitor */}
      <StaleLockMonitor />

      {/* Auto-update download progress toast */}
      <UpdateDownloadToast />

      {/* Auto-update ready prompt (restart to update) */}
      <UpdateReadyToast />

      {/* Auto-update failed repeatedly — quiet manual-download fallback */}
      <UpdateFallbackBanner />

      {/* Toast notifications */}
      <Toaster />

      {/* Crash dialog test harness (E2E tests only) */}
      <CrashDialogTestHarness />

      {/* Error boundary test harness (E2E tests only) */}
      <ErrorBoundaryTestHarness />

      {/* Main process crash listener (shows dialog for main process errors) */}
      <MainProcessCrashListener />

      {/* Global error listener (catches event handler errors, unhandled rejections) */}
      <GlobalErrorListener />

      {/* Global Table Explorer Dialog (can be triggered from anywhere via store) */}
      <GlobalTableExplorerDialog />

      {/* Global Database Blueprint setup dialog (Object Explorer → "Create Database Blueprint…", command palette, etc.) */}
      <GlobalDatabaseBlueprintSetupDialog />

      {/* Global Refresh-from-DB dialog (toolbar Refresh button, mounted-folder context menu). */}
      <GlobalRefreshFromDbDialog />

      {/* Global Sync-data dialog (toolbar Sync data button — Database Blueprint Slice 10b). */}
      <GlobalSyncDataDialog />

      {/* Global Sync-data progress dialog (post-Confirm orchestrator — Slice 10c). */}
      <GlobalSyncDataProgressDialog />

      {/* Global MetaInfo / Loose-relationships manager (opened from database context menu, Table Explorer toolbar, etc.) */}
      <GlobalMetaInfoManagerDialog />

      {/* VqeDdlEdit always-on preview dialog (opens before every Designer/inline-DDL edit commit — no byte threshold) */}
      <VqeDdlPreviewDialog />

      {/* VqeDdlEdit pre-destructive guard (Slice H3) — fires before any drop op with dependents enumerated by the always-on dependency-annotation surface. */}
      <VqeDdlBlastRadiusPanel
        onJumpToDependent={(item) => {
          const ws = useWorkspaceStore.getState();
          const activeTab = ws.tabs.find((t) => t.id === ws.activeTabId);
          openVqeDdlDependencyViewer({
            connectionId: activeTab?.connectionInfo?.connectionId,
            database: activeTab?.connectionInfo?.database ?? null,
            schema: item.schema,
            objectName: item.name,
            objectType: item.objectType,
          });
        }}
      />

      {/* Global Declare-as-JSON dialog (opened from grid header / cell context menus). */}
      <GlobalDeclareJsonColumnDialog />

      {/* Global Declare-as-Enum dialog (opened from grid header glyph / filter chip / MetaInfo manager). */}
      <GlobalDeclareEnumColumnDialog />

      {/* Global Declare-as-coordinates dialog (opened from grid header menu / glyph / MetaInfo manager). */}
      <GlobalDeclareSpatialColumnDialog />

      {/* Global JSON Inspector dialog (opened from grid context menus for
          already-declared JSON columns, plus the MetaInfo "Details" button). */}
      <GlobalJsonInspectorDialog />

      {/* Global Enum-values details dialog (opened from declared-enum header glyph
          plus the MetaInfo "Details" button). */}
      <GlobalEnumDetailsDialog />
      {/* Global Loose-FK editor dialog (opened from the column-header FK
          glyph, MetaInfo manager Details buttons, settings Details buttons). */}
      <GlobalLooseRelationshipEditorDialog />

      {/* Global ConfirmDialog (Radix AlertDialog mounted at app root so it
          never stacks inside another open Dialog — see confirmDialogStore). */}
      <GlobalConfirmDialog />
      <StorageCreateContainerDialog />
      <StorageDialogHost />
      <StorageTableDialogsHost />

      {/* MCP Write Approval Dialog (shown when AI agent requests to execute write queries) */}
      <McpWriteApprovalDialogContainer />
      <McpStorageWriteApprovalDialogContainer />

      {/* MCP Pairing Dialog (shown when CLI requests pairing) */}
      <McpPairingDialogContainer />

      {/* MCP Connections Add Approval Dialog (shown when AI agent requests to add a connection) */}
      <McpConnectionsAddApprovalDialogContainer />

      {/* MCP MetaInfo Declare Approval Dialog (shown when AI agent proposes a MetaInfo declaration) */}
      <McpMetaInfoDeclareApprovalDialogContainer />

      {/* Pro Upgrade Prompt Dialog (shown when user attempts Pro-only operation in Personal mode) */}
      <ProUpgradePromptDialog />

      {/* File drop overlay (visual feedback when dragging .sql or data files) */}
      <FileDropOverlay isDragging={isDragging} dragKind={dragKind} uploadLabel={uploadLabel} />

      {/* Connection diagnostics dialog (registers global handler at boot) */}
      <ConnectionDiagnosticsDialogHost />
      <QueueDialogsHost />
      <FileShareDialogsHost />

      {/* Pin-to-dashboard confirmation sheet (S20; opened via requestPinToDashboard) */}
      <PinConfirmSheet />
    </div>
    </TooltipProvider>
  );
}

export default App; 
